import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtCurrency, stageMeta, STAGES } from "@/lib/api";
import { useCurrencies, currencySymbol } from "@/lib/currency";
import { Search, Plus, UserPlus, Edit2, Trash2, FileText, Tag, X, ChevronDown, Mail, MessageCircle, History, CalendarClock } from "lucide-react";
import Modal from "@/components/Modal";
import CustomerForm from "@/components/CustomerForm";
import QuotationBuilder from "@/components/QuotationBuilder";
import OutreachModal from "@/components/OutreachModal";
import FollowupComposer from "@/components/FollowupComposer";
import StageChangeModal from "@/components/StageChangeModal";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function StagePill({ stage }) {
  const m = stageMeta(stage);
  return (
    <span className="tag" style={{ background: m.bg, borderColor: "var(--border)", color: m.accent }}>
      {m.label}
    </span>
  );
}

// Requirements are clipped to a single line in the table. Attach the hover
// tooltip only when the text is actually cut off, so short values don't get a
// redundant repeat of what's already on screen.
function ClampedText({ children, style }) {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setClipped(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);

  return (
    <div ref={ref} title={clipped ? children : undefined} style={style}>
      {children}
    </div>
  );
}

const emptyForm = () => ({ title: "", requirements: "", stage: "lead", tags: "" });
const emptyCreateForm = () => ({ customer_id: "", title: "", requirements: "", stage: "lead", tags: "" });

// Deals can hold different currencies, so aggregate totals are grouped by
// currency rather than summed together (₹ + $ can't be added meaningfully).
const sumByCurrency = (deals, field) => {
  const totals = {};
  for (const d of deals) {
    const c = d.currency || "INR";
    totals[c] = (totals[c] || 0) + (d[field] || 0);
  }
  return totals;
};

export default function Deals() {
  const { user, isAdmin } = useAuth();
  const currencies = useCurrencies();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  // Stage + last-activity date range can be pre-applied via query params
  // (e.g. from a stage header click on the Pipeline page).
  const [filterStage, setFilterStage] = useState(searchParams.get("stage") || "all");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");
  const [modal, setModal] = useState(null); // 'create' | 'edit' | 'quote'
  const [activeDeal, setActiveDeal] = useState(null);
  const [activeQuote, setActiveQuote] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const custRef = useRef(null);
  const [stageChange, setStageChange] = useState(null); // { deal, toStage } — remark/shipment prompt
  const [activities, setActivities] = useState([]); // full history for the deal open in the history modal
  const [note, setNote] = useState("");

  const openHistory = async (d) => {
    setActiveDeal(d);
    setModal("history");
    setNote("");
    setActivities([]);
    const r = await api.get("/activities", { params: { deal_id: d.id, limit: 500 } });
    setActivities(r.data);
  };

  const addNote = async () => {
    if (!note.trim() || !activeDeal) return;
    await api.post("/activities", { customer_id: activeDeal.customer_id, deal_id: activeDeal.id, type: "note", message: note.trim() });
    setNote("");
    const r = await api.get("/activities", { params: { deal_id: activeDeal.id, limit: 500 } });
    setActivities(r.data);
  };

  const load = () => Promise.all([
    api.get("/deals").then((r) => setDeals(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
  ]);

  useEffect(() => { load(); }, []);

  const customerMap = Object.fromEntries(customers.map((c) => [c.id, c]));

  // Salesperson sees only their customers; admin sees all
  const eligibleCustomers = isAdmin
    ? customers
    : customers.filter((c) => c.sales_person_id === user?.id);

  const custSuggestions = custSearch.trim().length === 0
    ? []
    : eligibleCustomers.filter((c) =>
        c.company?.toLowerCase().includes(custSearch.toLowerCase())
      );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (custRef.current && !custRef.current.contains(e.target)) setCustOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keep stage + date range reflected in the URL so the view is shareable and
  // survives a refresh (matches what the Pipeline stage links pass in).
  useEffect(() => {
    const next = {};
    if (filterStage !== "all") next.stage = filterStage;
    if (fromDate) next.from = fromDate;
    if (toDate) next.to = toDate;
    setSearchParams(next, { replace: true });
  }, [filterStage, fromDate, toDate, setSearchParams]);

  // "Last activity" is the deal's updated_at (bumped on every stage change / edit).
  const activityDate = (d) => (d.updated_at || d.created_at || "").slice(0, 10);

  const filtered = deals.filter((d) => {
    const q = search.toLowerCase();
    const matches = [d.title, d.requirements, d.customer_name, d.sales_person_name, ...(d.tags || [])]
      .some((v) => v?.toLowerCase().includes(q));
    const stageOk = filterStage === "all" || d.stage === filterStage;
    const day = activityDate(d);
    const fromOk = !fromDate || (day && day >= fromDate);
    const toOk = !toDate || (day && day <= toDate);
    return matches && stageOk && fromOk && toOk;
  });

  const clearDates = () => { setFromDate(""); setToDate(""); };

  const openEdit = (deal) => {
    setActiveDeal(deal);
    setEditForm({
      title: deal.title || "",
      requirements: deal.requirements || "",
      stage: deal.stage || "lead",
      tags: (deal.tags || []).join(", "),
    });
    setModal("edit");
  };

  const onSaveEdit = async () => {
    const stageChanged = editForm.stage !== activeDeal.stage;
    // Persist everything except the stage here; the stage move (with remark /
    // shipment) is handled by StageChangeModal so it's always documented.
    // FOB/CIF value is derived from the linked quotation, not edited here —
    // value/currency are intentionally omitted so they aren't clobbered.
    const payload = {
      title: editForm.title.trim(),
      requirements: editForm.requirements.trim(),
      tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    await api.patch(`/deals/${activeDeal.id}`, payload);
    if (stageChanged) {
      // Keep activeDeal in sync so the shipment gets the right customer info.
      setStageChange({ deal: { ...activeDeal, ...payload }, toStage: editForm.stage });
      setModal(null);
      return;
    }
    toast.success("Lead updated");
    setModal(null);
    setActiveDeal(null);
    load();
  };

  const onCreateDeal = async () => {
    if (!createForm.customer_id) { toast.error("Select a customer"); return; }
    if (!createForm.title.trim()) { toast.error("Title is required"); return; }
    const cust = customers.find((c) => c.id === createForm.customer_id);
    const payload = {
      customer_id: createForm.customer_id,
      title: createForm.title.trim(),
      requirements: createForm.requirements.trim(),
      stage: createForm.stage,
      tags: createForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      sales_person_id: cust?.sales_person_id || "",
      sales_person_name: cust?.sales_person_name || "",
    };
    await api.post("/deals", payload);
    toast.success("Lead created");
    setModal(null);
    setCreateForm(emptyCreateForm());
    setCustSearch("");
    setCustOpen(false);
    load();
  };

  const onAddCustomer = async (form) => {
    try {
      const { data: created } = await api.post("/customers", form);
      setCustomers((cs) => [...cs, created]);
      setCreateForm({ ...createForm, customer_id: created.id });
      setCustSearch(created.company);
      setCustOpen(false);
      setShowAddCustomer(false);
      toast.success("Customer added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add customer");
    }
  };

  const onDelete = async (deal) => {
    if (!window.confirm(`Delete lead "${deal.title || "Untitled"}"? This will also remove its quotations.`)) return;
    await api.delete(`/deals/${deal.id}`);
    toast.success("Lead deleted");
    load();
  };

  const activeDeals = filtered.filter((d) => d.stage !== "lost");
  const fobTotals = sumByCurrency(activeDeals, "fob_value");
  const cifTotals = sumByCurrency(activeDeals, "cif_value");
  const exworksTotals = sumByCurrency(activeDeals, "exworks_value");
  const totalsLabel = (totals) => Object.keys(totals).length === 0
    ? fmtCurrency(0, "INR", "₹")
    : Object.entries(totals).map(([code, v]) => fmtCurrency(v, code, currencySymbol(code, currencies))).join(" + ");

  return (
    <div data-testid="deals-page">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads…"
            className="input-base"
            style={{ paddingLeft: 36 }}
            data-testid="deals-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center" data-testid="stage-filter">
          <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Stage:</span>
          <button onClick={() => setFilterStage("all")} className={filterStage === "all" ? "btn-primary" : "btn-outline"} style={{ padding: "7px 14px", fontSize: 13 }} data-testid="filter-all">All</button>
          {STAGES.map((s) => (
            <button key={s.key} onClick={() => setFilterStage(s.key)} className={filterStage === s.key ? "btn-primary" : "btn-outline"} style={{ padding: "7px 14px", fontSize: 13 }} data-testid={`filter-${s.key}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center flex-wrap" data-testid="activity-date-filter">
          <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Last activity:</span>
          <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="input-base" style={{ padding: "6px 10px", fontSize: 13, width: "auto" }} data-testid="filter-from-date" />
          <span style={{ fontSize: 12, color: "var(--text-2)" }}>–</span>
          <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="input-base" style={{ padding: "6px 10px", fontSize: 13, width: "auto" }} data-testid="filter-to-date" />
          {(fromDate || toDate) && (
            <button onClick={clearDates} className="btn-ghost" title="Clear dates" style={{ padding: "4px 6px" }} data-testid="filter-clear-dates">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            Active FOB: <strong style={{ color: "var(--primary)" }}>{totalsLabel(fobTotals)}</strong>
          </div>
          <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            CIF: <strong style={{ color: "var(--primary)" }}>{totalsLabel(cifTotals)}</strong>
          </div>
          <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            Ex-Works: <strong style={{ color: "var(--primary)" }}>{totalsLabel(exworksTotals)}</strong>
          </div>
          <button
            onClick={() => { setCreateForm(emptyCreateForm()); setModal("create"); }}
            className="btn-primary"
            style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            data-testid="new-deal-btn"
          >
            <Plus size={14} /> New Lead
          </button>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="empty-state">
          <div style={{ fontSize: 14 }}>No leads match your filters.</div>
        </div>
      ) : (
        <div className="card-ledger overflow-hidden" data-testid="deals-list">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--surface-2)" }}>
                {["Lead", "Customer", "Sales Person", "Stage", "FOB", "CIF", "Ex-Works", ""].map((h, i) => (
                  <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: (i === 4 || i === 5 || i === 6) ? "right" : "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr
                  key={d.id}
                  className="stagger"
                  style={{ borderBottom: "1px solid var(--border)", animationDelay: `${i * 30}ms` }}
                  data-testid={`deal-row-${d.id}`}
                >
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.title || <span style={{ color: "var(--text-2)" }}>Untitled</span>}</div>
                    {d.requirements && (
                      <ClampedText style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {d.requirements}
                      </ClampedText>
                    )}
                    {(d.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {d.tags.map((t) => (
                          <span key={t} className="tag" style={{ fontSize: 9, padding: "1px 6px", background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-2)" }}>
                            <Tag size={9} /> {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }}>{d.customer_name || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{d.sales_person_name || "—"}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <StagePill stage={d.stage} />
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data">
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--primary)" }}>{fmtCurrency(d.fob_value, d.currency, currencySymbol(d.currency, currencies))}</span>
                    {d.currency && d.currency !== "INR" && (
                      <div style={{ fontSize: 10, color: "var(--text-2)", fontWeight: 500 }}>{d.currency}</div>
                    )}
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data">
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{fmtCurrency(d.cif_value, d.currency, currencySymbol(d.currency, currencies))}</span>
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} className="font-mono-data">
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{fmtCurrency(d.exworks_value, d.currency, currencySymbol(d.currency, currencies))}</span>
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }}>
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => { setActiveDeal(d); setModal("email"); }}
                        className="btn-ghost"
                        title="Send Email"
                        data-testid={`email-btn-${d.id}`}
                      >
                        <Mail size={14} />
                      </button>
                      <button
                        onClick={() => { setActiveDeal(d); setModal("whatsapp"); }}
                        className="btn-ghost"
                        style={{ color: "#25D366" }}
                        title="Send WhatsApp"
                        data-testid={`whatsapp-btn-${d.id}`}
                      >
                        <MessageCircle size={14} />
                      </button>
                      <button
                        onClick={() => { setActiveDeal(d); setModal("followup"); }}
                        className="btn-ghost"
                        title="Schedule Follow-up"
                        data-testid={`followup-btn-${d.id}`}
                      >
                        <CalendarClock size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          const r = await api.get(`/quotations?deal_id=${d.id}`);
                          setActiveQuote(r.data[0] || null);
                          setActiveDeal(d);
                          setModal("quote");
                        }}
                        className="btn-ghost"
                        title="Make Quotation"
                        data-testid={`quote-btn-${d.id}`}
                      >
                        <FileText size={14} />
                      </button>
                      <button
                        onClick={() => openHistory(d)}
                        className="btn-ghost"
                        title="Activity History"
                        data-testid={`history-btn-${d.id}`}
                      >
                        <History size={14} />
                      </button>
                      <button
                        onClick={() => openEdit(d)}
                        className="btn-ghost"
                        title="Edit"
                        data-testid={`edit-btn-${d.id}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(d)}
                        className="btn-ghost"
                        style={{ color: "var(--danger)" }}
                        title="Delete"
                        data-testid={`delete-btn-${d.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create deal modal */}
      {modal === "create" && (
        <Modal title="New Lead" subtitle="A sales opportunity in the pipeline" onClose={() => { setModal(null); setCustSearch(""); setCustOpen(false); }} size="md">
          <div className="flex flex-col gap-4" data-testid="deal-create-form">
            <div ref={custRef} style={{ position: "relative" }}>
              <div className="flex items-center justify-between">
                <label className="overline" style={{ fontSize: 10 }}>Customer</label>
                <button
                  type="button"
                  onClick={() => { setCustOpen(false); setShowAddCustomer(true); }}
                  className="btn-ghost"
                  style={{ fontSize: 11, color: "var(--primary)", padding: "2px 4px" }}
                  data-testid="create-deal-add-customer"
                >
                  <UserPlus size={12} /> Add new customer
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)", pointerEvents: "none" }} />
                <input
                  value={custSearch}
                  onChange={(e) => {
                    setCustSearch(e.target.value);
                    setCreateForm({ ...createForm, customer_id: "" });
                    setCustOpen(true);
                  }}
                  onFocus={() => { if (custSearch.trim()) setCustOpen(true); }}
                  placeholder="Search customer…"
                  className="input-base"
                  style={{ paddingLeft: 32 }}
                  autoComplete="off"
                  data-testid="create-deal-customer"
                />
                {custSearch && (
                  <button
                    type="button"
                    onClick={() => { setCustSearch(""); setCreateForm({ ...createForm, customer_id: "" }); setCustOpen(false); }}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-2)", padding: 2 }}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              {custOpen && custSuggestions.length > 0 && (
                <div className="card-ledger" style={{ position: "absolute", zIndex: 50, left: 0, right: 0, top: "calc(100% + 4px)", padding: 0, overflow: "hidden" }}>
                  {custSuggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setCreateForm({ ...createForm, customer_id: c.id });
                        setCustSearch(c.company);
                        setCustOpen(false);
                      }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--border)" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "var(--surface-2)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                    >
                      <span style={{ fontWeight: 600 }}>{c.company}</span>
                      {c.sales_person_name && <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-2)" }}>{c.sales_person_name}</span>}
                    </button>
                  ))}
                </div>
              )}
              {custOpen && custSearch.trim().length > 0 && custSuggestions.length === 0 && (
                <div className="card-ledger" style={{ position: "absolute", zIndex: 50, left: 0, right: 0, top: "calc(100% + 4px)", padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px", fontSize: 13, color: "var(--text-2)" }}>No customers found</div>
                  <button
                    type="button"
                    onClick={() => setShowAddCustomer(true)}
                    style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", borderTop: "1px solid var(--border)", cursor: "pointer", fontSize: 13, color: "var(--primary)" }}
                  >
                    <UserPlus size={13} /> Add "{custSearch.trim()}" as a new customer
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Title</label>
              <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="e.g. A4 Copier Paper — Q3 reorder" className="input-base" data-testid="create-deal-title" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Requirements</label>
              <textarea value={createForm.requirements} onChange={(e) => setCreateForm({ ...createForm, requirements: e.target.value })} placeholder="What the customer needs…" className="input-base" rows={3} data-testid="create-deal-requirements" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Stage</label>
              <select value={createForm.stage} onChange={(e) => setCreateForm({ ...createForm, stage: e.target.value })} className="input-base" data-testid="create-deal-stage">
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Tags (comma-separated)</label>
              <input value={createForm.tags} onChange={(e) => setCreateForm({ ...createForm, tags: e.target.value })} placeholder="export, recurring" className="input-base" data-testid="create-deal-tags" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setModal(null); setCustSearch(""); setCustOpen(false); }} className="btn-outline">Cancel</button>
              <button onClick={onCreateDeal} className="btn-primary" data-testid="create-deal-save">Create Lead</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add customer modal (from within New Deal) */}
      {showAddCustomer && (
        <Modal title="Add Customer" subtitle="Capture lead or account details" onClose={() => setShowAddCustomer(false)} size="lg">
          <CustomerForm
            initial={{ company: custSearch.trim(), sales_person_id: user?.id || "", sales_person_name: user?.name || "" }}
            onSave={onAddCustomer}
            onCancel={() => setShowAddCustomer(false)}
          />
        </Modal>
      )}

      {/* Edit deal modal */}
      {modal === "edit" && activeDeal && (
        <Modal title={`Edit Lead · ${activeDeal.customer_name}`} onClose={() => { setModal(null); setActiveDeal(null); }} size="md">
          <div className="flex flex-col gap-4" data-testid="deal-edit-form">
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Title</label>
              <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="e.g. A4 Copier Paper — Q3 reorder" className="input-base" data-testid="edit-deal-title" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Requirements</label>
              <textarea value={editForm.requirements} onChange={(e) => setEditForm({ ...editForm, requirements: e.target.value })} placeholder="What the customer needs…" className="input-base" rows={3} data-testid="edit-deal-requirements" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Stage</label>
              <select value={editForm.stage} onChange={(e) => setEditForm({ ...editForm, stage: e.target.value })} className="input-base" data-testid="edit-deal-stage">
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Tags (comma-separated)</label>
              <input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} placeholder="export, recurring" className="input-base" data-testid="edit-deal-tags" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setModal(null); setActiveDeal(null); }} className="btn-outline">Cancel</button>
              <button onClick={onSaveEdit} className="btn-primary" data-testid="edit-deal-save">Save Changes</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Quotation modal */}
      {modal === "quote" && activeDeal && (
        <Modal
          title={`Quotation · ${activeDeal.customer_name}`}
          subtitle="quote"
          onClose={() => { setModal(null); setActiveDeal(null); setActiveQuote(null); }}
          size="xl"
        >
          <QuotationBuilder
            customer={customerMap[activeDeal.customer_id] || { id: activeDeal.customer_id, company: activeDeal.customer_name }}
            deal={activeDeal}
            existingQuote={activeQuote}
            onClose={() => { setModal(null); setActiveDeal(null); setActiveQuote(null); }}
            onSaved={() => { setModal(null); setActiveDeal(null); setActiveQuote(null); load(); }}
          />
        </Modal>
      )}

      {/* Full activity history for a single deal */}
      {modal === "history" && activeDeal && (
        <Modal
          title={`Activity History · ${activeDeal.title || "Untitled"}`}
          subtitle={activeDeal.customer_name || ""}
          onClose={() => { setModal(null); setActiveDeal(null); setActivities([]); setNote(""); }}
          size="md"
        >
          <div data-testid="deal-history">
            <div className="flex gap-2 mb-3">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a quick note…"
                className="input-base"
                data-testid="deal-note-input"
                onKeyDown={(e) => e.key === "Enter" && addNote()}
              />
              <button onClick={addNote} className="btn-outline" data-testid="deal-note-add">Add</button>
            </div>
            {activities.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-2)", padding: "16px 0" }}>No activity recorded yet.</div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: "50vh", overflowY: "auto" }}>
                {activities.map((a) => (
                  <li key={a.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 6, height: 6, borderRadius: 6, background: "var(--primary)", marginTop: 8, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{a.message}</div>
                      <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>
                        {a.type} · {new Date(a.created_at).toLocaleString()}{a.by_name ? ` · ${a.by_name}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}

      {/* Email / WhatsApp outreach modals */}
      {(modal === "email" || modal === "whatsapp") && activeDeal && (
        <OutreachModal
          channel={modal}
          deal={activeDeal}
          customer={customerMap[activeDeal.customer_id] || { id: activeDeal.customer_id, company: activeDeal.customer_name }}
          onClose={() => { setModal(null); setActiveDeal(null); }}
        />
      )}

      {/* Schedule a stage-aware follow-up */}
      {modal === "followup" && activeDeal && (
        <FollowupComposer
          deal={activeDeal}
          customer={customerMap[activeDeal.customer_id] || { id: activeDeal.customer_id, company: activeDeal.customer_name }}
          onClose={() => { setModal(null); setActiveDeal(null); }}
          onScheduled={() => { setModal(null); setActiveDeal(null); load(); }}
        />
      )}

      {/* Stage-change remark + sample shipment prompt */}
      {stageChange && (
        <StageChangeModal
          deal={stageChange.deal}
          fromStage={stageChange.deal.stage}
          toStage={stageChange.toStage}
          onCancel={() => { setStageChange(null); setActiveDeal(null); load(); }}
          onDone={() => { setStageChange(null); setActiveDeal(null); load(); }}
        />
      )}
    </div>
  );
}
